import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Logo, Ornament } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { trackPageView, trackCtaClick } from "@/lib/analytics";
import { SPLASH } from "@/constants/testIds";

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    trackPageView();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/email-signups", { email, source: "splash" });
      trackCtaClick("splash_email_capture");
      setDone(true);
      toast.success("Your invitation is reserved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={SPLASH.root}
      className="relative min-h-screen flex flex-col items-center justify-center jewel-bg text-ivory overflow-hidden grain-overlay"
      style={{ color: "var(--ivory)" }}
    >
      {/* Corner ornaments */}
      <CornerOrnaments />

      <div className="relative z-10 max-w-2xl w-full px-6 text-center flex flex-col items-center">
        <div className="reveal-up" data-testid={SPLASH.logo}>
          <Logo size={220} />
        </div>

        <div
          className="mt-8 text-[10px] tracking-[0.55em] uppercase reveal-up"
          style={{ color: "#D4AF37", animationDelay: "80ms" }}
        >
          Presenting the originals of Tom Pergola
        </div>

        <h1
          data-testid={SPLASH.heading}
          className="font-serif mt-8 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight reveal-up"
          style={{ animationDelay: "180ms" }}
        >
          A private unveiling of{" "}
          <em className="italic" style={{ color: "#D4AF37" }}>
            Tom Pergola
          </em>
          .
        </h1>

        <p
          className="mt-6 text-sm sm:text-base max-w-lg text-ivory/80 reveal-up"
          style={{ animationDelay: "260ms" }}
        >
          Originals from the studio are being prepared for the gala. Reserve
          your invitation and be first to view — and first to acquire.
        </p>

        <div className="my-8 w-52 reveal-up" style={{ animationDelay: "340ms" }}>
          <Ornament color="#D4AF37" />
        </div>

        <Countdown target="2026-07-08T19:00:00-04:00" />

        {done ? (
          <div
            data-testid={SPLASH.successMessage}
            className="reveal-up font-serif text-2xl"
            style={{ color: "#D4AF37" }}
          >
            Thank you — your invitation is reserved.
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="w-full max-w-md flex flex-col sm:flex-row gap-3 reveal-up"
            style={{ animationDelay: "420ms" }}
          >
            <Input
              type="email"
              required
              data-testid={SPLASH.emailInput}
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none bg-transparent border-0 border-b h-12 text-ivory placeholder:text-ivory/40 focus-visible:ring-0 focus-visible:border-b-2"
              style={{ borderColor: "#D4AF37", color: "var(--ivory)" }}
            />
            <button
              data-testid={SPLASH.submitBtn}
              type="submit"
              disabled={submitting}
              className="btn-gold px-6 h-12 tracking-[0.25em] text-xs uppercase font-medium"
            >
              {submitting ? "Reserving…" : "Reserve invitation"}
            </button>
          </form>
        )}

        <Link
          to="/"
          onClick={() => trackCtaClick("splash_enter_site")}
          data-testid={SPLASH.enterSiteLink}
          className="mt-10 text-[10px] tracking-[0.4em] uppercase transition-colors hover:text-ivory"
          style={{ color: "rgba(212,175,55,0.7)" }}
        >
          Enter the House ↗
        </Link>

        <div className="mt-16 text-[10px] tracking-[0.5em] uppercase text-ivory/40">
          Gala evening · 8 July 2026
        </div>
      </div>
    </div>
  );
}

function Countdown({ target }) {
  const targetTs = useMemo(() => new Date(target).getTime(), [target]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, targetTs - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const cells = [
    { k: "Days", v: days },
    { k: "Hours", v: hours },
    { k: "Minutes", v: mins },
    { k: "Seconds", v: secs },
  ];
  return (
    <div
      className="mb-8 grid grid-cols-4 gap-3 sm:gap-6 max-w-md w-full reveal-up"
      style={{ animationDelay: "380ms" }}
      data-testid="splash-countdown"
    >
      {cells.map((c) => (
        <div key={c.k} className="text-center">
          <div
            className="font-serif text-3xl sm:text-5xl leading-none"
            style={{ color: "#D4AF37" }}
          >
            {String(c.v).padStart(2, "0")}
          </div>
          <div className="mt-2 text-[9px] tracking-[0.35em] uppercase text-ivory/50">
            {c.k}
          </div>
        </div>
      ))}
    </div>
  );
}

function CornerOrnaments() {
  const stroke = "rgba(212,175,55,0.4)";
  return (
    <>
      <svg
        className="absolute top-6 left-6"
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
      >
        <path d="M4 30 Q4 4 30 4" stroke={stroke} strokeWidth="1" />
        <path d="M10 46 Q10 10 46 10" stroke={stroke} strokeWidth="0.5" />
      </svg>
      <svg
        className="absolute top-6 right-6 rotate-90"
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
      >
        <path d="M4 30 Q4 4 30 4" stroke={stroke} strokeWidth="1" />
        <path d="M10 46 Q10 10 46 10" stroke={stroke} strokeWidth="0.5" />
      </svg>
      <svg
        className="absolute bottom-6 left-6 -rotate-90"
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
      >
        <path d="M4 30 Q4 4 30 4" stroke={stroke} strokeWidth="1" />
        <path d="M10 46 Q10 10 46 10" stroke={stroke} strokeWidth="0.5" />
      </svg>
      <svg
        className="absolute bottom-6 right-6 rotate-180"
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
      >
        <path d="M4 30 Q4 4 30 4" stroke={stroke} strokeWidth="1" />
        <path d="M10 46 Q10 10 46 10" stroke={stroke} strokeWidth="0.5" />
      </svg>
    </>
  );
}
